import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  Skeleton,
  LinearProgress,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  SwapHoriz as SwapIcon,
  Clear as ClearIcon,
  LockOutlined as LockIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isOtherCustomer } from '../utils/auth';
import { DataTable, type Column, type SortDirection } from '../components/common';
import { modelChangeExplanation } from '../components/reliability/ReliabilityTraceView';
import { WarningChips } from '../components/performance/common';
import { UsageReport } from '../components/performance/UsageReport';
import { ChainListRow, FunctionWarning, performanceApi } from '../services/performanceApi';
import { requestIdIn, requestLookupPath } from '../services/requestLookupApi';

const STATS_DAYS = 30;
const SEARCH_DEBOUNCE_MS = 350;

interface FunctionStats {
  function_id: string;
  function_name: string;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  average_duration_ms: number;
  last_run?: string;
  most_used_model?: string;
  success_rate?: number;
}

export interface RunRecord {
  run_type?: 'function' | 'chain';
  id: string;
  function_id: string;
  function_name: string;
  created_at: string;
  execution_time_ms: number;
  success: boolean;
  model_used?: string | null;
  requested_model?: string | null;
  selected_model?: string | null;
  model_changed?: boolean;
  model_change_reason?: 'zdr_substitute' | 'reliability_fallback' | null;
  reliability?: { mode?: string; result?: string; attempts?: number; time_limit_s?: number | null; elapsed_s?: number | null } | null;
  input_type: string;
  error?: string;
  tokens?: number;
  ai_credits?: number;
  chain_id?: string;
  chain_name?: string;
  content_redacted?: boolean;
  is_test?: boolean;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

const formatDuration = (ms?: number | null) => {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

export const ModelCell: React.FC<{ row: RunRecord }> = ({ row }) => {
  if (!row.model_used) return <>-</>;
  const attempts = row.reliability?.attempts ?? 0;
  return (
    <Stack spacing={0.25} alignItems="flex-start">
      <Chip label={row.model_used} size="small" variant="outlined" />
      {row.model_changed && row.requested_model && row.requested_model !== row.model_used && (
        <Tooltip title={modelChangeExplanation(row)}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'warning.main', cursor: 'help' }}>
            <SwapIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" noWrap>
              requested {row.requested_model}
            </Typography>
          </Stack>
        </Tooltip>
      )}
      {attempts > 1 && !row.model_changed && (
        <Typography variant="caption" color="text.secondary">
          {attempts} attempts
        </Typography>
      )}
    </Stack>
  );
};

export const FunctionRunHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user, permissions, getIdToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'reports' ? 'reports' : 'runs';
  const [warnings, setWarnings] = useState<Record<string, FunctionWarning[]>>({});
  const [warningDays, setWarningDays] = useState(7);
  const [chainStats, setChainStats] = useState<ChainListRow[]>([]);

  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [functionStats, setFunctionStats] = useState<FunctionStats[]>([]);
  const [statsSearch, setStatsSearch] = useState('');

  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [recentRuns, setRecentRuns] = useState<RunRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [runSearch, setRunSearch] = useState('');
  const [filterModel, setFilterModel] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [includeTests, setIncludeTests] = useState(false);
  const [knownModels, setKnownModels] = useState<string[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  const debouncedRunSearch = useDebounced(runSearch.trim(), SEARCH_DEBOUNCE_MS);
  const runsRequest = useRef<AbortController | null>(null);

  const customerId = searchParams.get('customer_id');
  const customerTag = searchParams.get('customer_tag');
  const isViewingOtherCustomer = isOtherCustomer(customerId, permissions);

  const rememberModels = useCallback((models: (string | null | undefined)[]) => {
    const fresh = models.filter((m): m is string => Boolean(m));
    if (!fresh.length) return;
    setKnownModels(prev => {
      const merged = new Set(prev);
      fresh.forEach(m => merged.add(m));
      return merged.size === prev.length ? prev : Array.from(merged).sort();
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const idToken = await user.getIdToken();
        const url = isViewingOtherCustomer
          ? `/api/v2/analytics/admin/ai-functions?customer_id=${encodeURIComponent(customerId!)}&days=${STATS_DAYS}`
          : `/api/v2/analytics/ai-functions?days=${STATS_DAYS}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
        if (!res.ok) throw new Error('Failed to load function statistics');
        const body = await res.json();
        if (cancelled) return;
        const functions: FunctionStats[] = body.data?.functions || [];
        setFunctionStats(functions);
        rememberModels(functions.map(f => f.most_used_model));
      } catch (err) {
        if (!cancelled) setStatsError(err instanceof Error ? err.message : 'Failed to load statistics');
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isViewingOtherCustomer, customerId, reloadKey, rememberModels]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    performanceApi(getIdToken)
      .warnings(isViewingOtherCustomer ? customerId : null)
      .then(body => {
        if (cancelled) return;
        setWarnings(body.functions);
        setWarningDays(body.window_days);
      })
      .catch(() => {
        if (!cancelled) setWarnings({});
      });
    return () => {
      cancelled = true;
    };
  }, [user, getIdToken, isViewingOtherCustomer, customerId, reloadKey]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    performanceApi(getIdToken)
      .chains(isViewingOtherCustomer ? customerId : null)
      .then(body => !cancelled && setChainStats(body.chains))
      .catch(() => !cancelled && setChainStats([]));
    return () => {
      cancelled = true;
    };
  }, [user, getIdToken, isViewingOtherCustomer, customerId, reloadKey]);

  useEffect(() => {
    if (!user) return;
    runsRequest.current?.abort();
    const controller = new AbortController();
    runsRequest.current = controller;

    (async () => {
      setRunsLoading(true);
      setRunsError(null);
      try {
        const idToken = await user.getIdToken();
        const params = new URLSearchParams({
          page: String(page + 1),
          page_size: String(rowsPerPage),
          sort_by: sortBy,
          sort_dir: sortDir,
        });
        if (isViewingOtherCustomer) params.set('customer_id', customerId!);
        if (filterModel !== 'all') params.set('model_filter', filterModel);
        if (filterStatus !== 'all') params.set('status_filter', filterStatus);
        if (includeTests) params.set('include_tests', 'true');
        if (debouncedRunSearch) params.set('search', debouncedRunSearch);
        const base = isViewingOtherCustomer ? '/api/v2/analytics/admin/recent-runs' : '/api/v2/analytics/recent-runs';
        const res = await fetch(`${base}?${params}`, {
          headers: { Authorization: `Bearer ${idToken}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('Failed to load recent runs');
        const body = await res.json();
        if (controller.signal.aborted) return;
        const results: RunRecord[] = body.results || [];
        setRecentRuns(results);
        setTotalCount(body.pagination?.total_items || 0);
        rememberModels(results.flatMap(r => [r.model_used, r.requested_model]));
      } catch (err) {
        if (controller.signal.aborted) return;
        setRunsError(err instanceof Error ? err.message : 'Failed to load recent runs');
      } finally {
        if (!controller.signal.aborted) setRunsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [
    user,
    isViewingOtherCustomer,
    customerId,
    page,
    rowsPerPage,
    sortBy,
    sortDir,
    filterModel,
    filterStatus,
    includeTests,
    debouncedRunSearch,
    reloadKey,
    rememberModels,
  ]);

  useEffect(() => {
    setPage(0);
  }, [debouncedRunSearch]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(column === 'function_name' || column === 'model_used' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const openRun = (row: RunRecord) => {
    if (row.run_type === 'chain') {
      navigate(`/dashboard/chain-run/${row.id}`, { state: { run: row } });
    } else {
      navigate(`/dashboard/run/${row.id}`, { state: { run: row } });
    }
  };

  const totals = functionStats.reduce(
    (acc, f) => {
      acc.runs += f.total_runs;
      acc.ok += f.successful_runs;
      acc.failed += f.failed_runs;
      acc.time += (f.average_duration_ms || 0) * f.total_runs;
      return acc;
    },
    { runs: 0, ok: 0, failed: 0, time: 0 }
  );

  const getSuccessRate = (stats: FunctionStats) =>
    stats.total_runs === 0 ? 0 : Math.round((stats.successful_runs / stats.total_runs) * 100);

  const filteredStats = functionStats.filter(stat =>
    stat.function_name.toLowerCase().includes(statsSearch.toLowerCase())
  );

  const searchPending = runSearch.trim() !== debouncedRunSearch;
  const searchedRequestId = requestIdIn(runSearch);
  const filtersActive = Boolean(runSearch) || filterModel !== 'all' || filterStatus !== 'all';

  const recentRunColumns: Column<RunRecord>[] = [
    {
      id: 'function_name',
      label: 'Run',
      sortable: true,
      render: (value, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.run_type === 'chain' && row.chain_name ? row.chain_name : value}
          </Typography>
          {(row.run_type === 'chain' || row.is_test) && (
            <Typography variant="caption" color="text.secondary">
              {[row.run_type === 'chain' && 'Chain', row.is_test && 'Test run'].filter(Boolean).join(' · ')}
            </Typography>
          )}
          {row.content_redacted && (
            <Tooltip title="Input and output were not kept (zero data retention or the account's data retention setting). Usage is still recorded.">
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', cursor: 'help' }}>
                <LockIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption">Content not stored</Typography>
              </Stack>
            </Tooltip>
          )}
        </Box>
      ),
    },
    { id: 'created_at', label: 'Time', sortable: true, render: value => formatDate(value) },
    { id: 'execution_time_ms', label: 'Duration', sortable: true, render: value => formatDuration(value) },
    { id: 'model_used', label: 'Model', sortable: true, render: (_, row) => <ModelCell row={row} /> },
    { id: 'input_type', label: 'Input', sortable: false },
    {
      id: 'tokens',
      label: 'Tokens',
      sortable: true,
      align: 'right',
      render: value => (value ? Number(value).toLocaleString() : '-'),
    },
    {
      id: 'ai_credits',
      label: 'AI Credits',
      sortable: false,
      align: 'right',
      render: value => (value ? Number(value).toFixed(2) : '-'),
    },
    {
      id: 'success',
      label: 'Status',
      sortable: false,
      align: 'center',
      render: (value, row) =>
        value ? (
          <Chip icon={<SuccessIcon />} label="Success" color="success" size="small" />
        ) : (
          <Tooltip title={row.error || 'Failed'}>
            <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
          </Tooltip>
        ),
    },
    {
      id: 'actions',
      label: '',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Tooltip title={row.run_type === 'chain' ? 'View chain run details' : 'View full run details'}>
          <IconButton size="small" onClick={() => openRun(row)}>
            <ViewIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const statCard = (icon: React.ReactNode, label: string, value: string) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography color="textSecondary" gutterBottom>
            {label}
          </Typography>
        </Box>
        <Typography variant="h4">{statsLoading ? <Skeleton width={90} /> : value}</Typography>
        <Typography variant="caption" color="text.secondary">
          Last {STATS_DAYS} days
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Runs & Performance</Typography>
          {isViewingOtherCustomer && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Viewing runs for customer: {customerTag || customerId}
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => setReloadKey(k => k + 1)} disabled={statsLoading || runsLoading}>
              {statsLoading || runsLoading ? <CircularProgress size={22} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {isViewingOtherCustomer && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are viewing run history for {customerTag ? `"${customerTag}"` : 'another customer'} as an admin.
        </Alert>
      )}

      {statsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {statsError}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          {statCard(<TrendingUpIcon color="primary" />, 'Total Runs', totals.runs.toLocaleString())}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {statCard(
            <SuccessIcon color="success" />,
            'Success Rate',
            totals.runs ? `${Math.round((totals.ok / totals.runs) * 100)}%` : '–'
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {statCard(
            <TimeIcon color="info" />,
            'Avg Response Time',
            totals.runs ? formatDuration(totals.time / totals.runs) : '–'
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {statCard(<ErrorIcon color="error" />, 'Failed Runs', totals.failed.toLocaleString())}
        </Grid>
      </Grid>

      <Tabs
        value={view}
        onChange={(_, v) => {
          const next = new URLSearchParams(searchParams);
          if (v === 'reports') next.set('view', 'reports');
          else next.delete('view');
          setSearchParams(next, { replace: true });
        }}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="runs" label="Functions & runs" />
        <Tab value="reports" label="Reports" />
      </Tabs>

      {view === 'reports' && <UsageReport customerId={isViewingOtherCustomer ? customerId : null} />}

      {view === 'runs' && (
      <>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6">Function Performance</Typography>
            <Typography variant="caption" color="text.secondary">
              Last {STATS_DAYS} days
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search functions..."
            value={statsSearch}
            onChange={e => setStatsSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Function Name</TableCell>
                <TableCell align="right">Total Runs</TableCell>
                <TableCell align="right">Success Rate</TableCell>
                <TableCell align="right">Avg Time</TableCell>
                <TableCell>Most Used Model</TableCell>
                <TableCell>
                  <Tooltip title={`Compared with the previous ${warningDays} days. Hover a badge for details.`}>
                    <span style={{ cursor: 'help' }}>Warnings ({warningDays}d)</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Last Run</TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {statsLoading ? (
                [0, 1, 2].map(i => (
                  <TableRow key={i}>
                    <TableCell colSpan={8}>
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredStats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No function runs in the last {STATS_DAYS} days
                  </TableCell>
                </TableRow>
              ) : (
                filteredStats.map(stat => (
                  <TableRow key={stat.function_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {stat.function_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{stat.total_runs.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${getSuccessRate(stat)}%`}
                        color={getSuccessRate(stat) >= 90 ? 'success' : getSuccessRate(stat) >= 70 ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{formatDuration(stat.average_duration_ms)}</TableCell>
                    <TableCell>
                      {stat.most_used_model ? <Chip label={stat.most_used_model} size="small" variant="outlined" /> : '-'}
                    </TableCell>
                    <TableCell>
                      {warnings[stat.function_name]?.length ? (
                        <WarningChips warnings={warnings[stat.function_name]} max={2} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          –
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{stat.last_run ? formatDate(stat.last_run) : 'Never'}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {(['runs', 'performance'] as const).map(target => (
                        <Tooltip key={target} title={target === 'runs' ? "View this function's runs" : 'Performance: latency, time limit, outcomes, cost'}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const params = new URLSearchParams();
                              if (customerId) params.append('customer_id', customerId);
                              if (customerTag) params.append('customer_tag', customerTag);
                              if (target === 'performance') params.append('tab', 'performance');
                              const queryString = params.toString();
                              navigate(`/dashboard/function-runs/${stat.function_id}${queryString ? '?' + queryString : ''}`);
                            }}
                          >
                            {target === 'runs' ? <ViewIcon /> : <ChartIcon />}
                          </IconButton>
                        </Tooltip>
                      ))}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {chainStats.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Chain Performance</Typography>
            <Typography variant="caption" color="text.secondary">
              Last {warningDays} days · finished runs, reruns from a step left out
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Chain</TableCell>
                  <TableCell align="right">Runs</TableCell>
                  <TableCell align="right">Completed</TableCell>
                  <TableCell align="right">Typical / p95</TableCell>
                  <TableCell>
                    <Tooltip title={`Compared with the previous ${warningDays} days. Hover a badge for details.`}>
                      <span style={{ cursor: 'help' }}>Warnings ({warningDays}d)</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {chainStats.map(c => {
                  const rate = c.success_rate === null ? null : Math.round(c.success_rate * 100);
                  return (
                    <TableRow key={c.chain_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {c.chain_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{c.runs.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {rate === null ? '-' : <Chip label={`${rate}%`} color={rate >= 90 ? 'success' : rate >= 70 ? 'warning' : 'error'} size="small" />}
                      </TableCell>
                      <TableCell align="right">
                        {formatDuration(c.p50_ms ?? undefined)} / {formatDuration(c.p95_ms ?? undefined)}
                      </TableCell>
                      <TableCell>
                        {c.warnings.length ? (
                          <WarningChips warnings={c.warnings} max={2} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            –
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Performance: run times, steps, time limit, cost">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/dashboard/chain-performance/${c.chain_id}${isViewingOtherCustomer && customerId ? `?customer_id=${encodeURIComponent(customerId)}` : ''}`)
                            }
                          >
                            <ChartIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6">Recent Runs</Typography>
            <Typography variant="caption" color="text.secondary">
              {runsLoading && !recentRuns.length
                ? 'Loading…'
                : `${totalCount.toLocaleString()} run${totalCount === 1 ? '' : 's'}${filtersActive ? ' match your filters' : ''}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search function or chain name..."
              value={runSearch}
              onChange={e => setRunSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searchPending ? <CircularProgress size={18} /> : <SearchIcon />}
                  </InputAdornment>
                ),
                endAdornment: runSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRunSearch('')} aria-label="Clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Model</InputLabel>
              <Select
                value={filterModel}
                onChange={e => {
                  setFilterModel(e.target.value);
                  setPage(0);
                }}
                label="Model"
              >
                <MenuItem value="all">All models</MenuItem>
                {knownModels.map(model => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={e => {
                  setFilterStatus(e.target.value);
                  setPage(0);
                }}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Runs made by test cases are billed, but left out of this list, stats, time limits and learning unless you include them here.">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeTests}
                    onChange={e => {
                      setIncludeTests(e.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label={<Typography variant="body2">Test runs</Typography>}
                sx={{ ml: 0.5, mr: 0 }}
              />
            </Tooltip>
          </Box>
        </Box>

        {searchedRequestId && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(requestLookupPath(searchedRequestId, isViewingOtherCustomer ? customerId : null))}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Look up platform request {searchedRequestId}
              </Button>
            }
          >
            This looks like a platform request ID. This search only matches function and chain names.
          </Alert>
        )}
        {filterModel !== 'all' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Chain runs are hidden while a model filter is set (a chain can use several models).
          </Typography>
        )}
        {runsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {runsError}
          </Alert>
        )}

        <Box sx={{ position: 'relative' }}>
          {runsLoading && recentRuns.length > 0 && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
          )}
          <Box sx={{ opacity: runsLoading && recentRuns.length > 0 ? 0.6 : 1, transition: 'opacity 120ms' }}>
            <DataTable
              columns={recentRunColumns}
              data={recentRuns}
              loading={runsLoading && recentRuns.length === 0}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sortBy={sortBy}
              sortDirection={sortDir}
              onSort={handleSort}
              rowKey="id"
              emptyMessage={filtersActive ? 'No runs match these filters' : 'No runs yet'}
            />
          </Box>
        </Box>
      </Paper>
      </>
      )}
    </Box>
  );
};
